import { Err, Ok } from '../../result/shared/result.js';
import { DEFAULT_VERSION, CLIENT_GLOBAL_OBJECT_KEY, CORE_APP_ENV_KEYS_ATTR_MAP, CORE_APP_ENV_KEYS_QUERY_MAP, CLIENT_APP_ENV_DUMP_KEY } from '../shared/const.js';
import { findMcsExecutionEnvironmentType } from '../shared/utils.js';
import { Some, None } from '../../option/shared/option.js';
import { ConfigurationError, InitializationError } from '../../errors/shared/base.js';
import { intoIterable, iteratorMap } from '../../iterator/shared/iterator.js';
import { createBaseStdoutLogger } from '../../logger/browser/baseStdoutLogger.js';

const DEFAULT_MCS_RUN_ENV = 'unknown';
const NAMESPACE = 'core.env.browser';
const logger = createBaseStdoutLogger(NAMESPACE);
let initializer = None();
let waiter = None();
const waiterQueue = new Set();
let appEnv = None();
/**
 * Возвращает ссылку на singleton
 *
 * @export
 * @return {GenericErrResult<IAppEnv>}
 */
function initAppEnv() {
    if (appEnv.isSome()) {
        return Ok(appEnv.Some);
    }
    const currentScriptEnvironmentResult = getAppEnvInitParams();
    if (currentScriptEnvironmentResult.isErr()) {
        return currentScriptEnvironmentResult;
    }
    return Ok(createAppEnv(currentScriptEnvironmentResult.Ok));
}
/**
 * Возвращает ссылку на singleton
 *
 * @export
 * @return {Result<IAppEnv, ConfigurationError>}
 */
function getAppEnv() {
    if (appEnv.isNone()) {
        return Err(new ConfigurationError({
            message: 'AppEnv не инициализирован! Необходимо вызвать метод initAppEnv()',
            domain: NAMESPACE + '.getAppEnv',
        }));
    }
    return Ok(appEnv.Some);
}
function createAppEnv(params) {
    if (appEnv.isSome()) {
        return appEnv.Some;
    }
    const { application, version: versionFromScript, mcsRunEnv: mcsRunEnvFromScript, versionCommit, extraDataParameters = new Map(), extraUrlParameters = new Map(), currentSrcUrl, } = params;
    const version = versionFromScript || DEFAULT_VERSION;
    const mcsRunEnv = mcsRunEnvFromScript || DEFAULT_MCS_RUN_ENV;
    const mcsRunEnvType = findMcsExecutionEnvironmentType(version, mcsRunEnv);
    const { availWidth, availHeight, width, height } = globalThis.screen;
    appEnv = Some({
        application,
        availableScreenSize: `${availWidth}/${availHeight}`,
        href: globalThis.location.href,
        mcsRunEnv,
        mcsRunEnvType,
        screenSize: `${width}/${height}`,
        version,
        extraDataParameters,
        extraUrlParameters,
        currentSrcUrl,
        versionCommit,
    });
    if (initializer.isSome()) {
        initializer.Some();
    }
    return appEnv.Some;
}
function waitForAppEnvInit(onInit) {
    if (appEnv.isSome()) {
        return;
    }
    let et;
    if (waiter.isNone()) {
        et = new EventTarget();
        waiter = Some(et);
        initializer = Some(() => {
            et.dispatchEvent(new Event('init'));
            afterAppEnvInit();
        });
    }
    else {
        et = waiter.Some;
    }
    // TODO: здесь могут быть утечки памяти, на момент инициализации appEnv объект, который ожидал
    // инициализацию мог быть удален
    et.addEventListener('init', onInit);
    waiterQueue.add(onInit);
}
/**
 * Предназначено для тестов
 *
 * @private
 */
function __cleanAppEnv() {
    if (appEnv) {
        appEnv = None();
    }
    afterAppEnvInit();
}
function afterAppEnvInit() {
    if (initializer.isSome()) {
        if (waiter.isSome()) {
            for (const handler of waiterQueue) {
                waiter.Some.removeEventListener('init', handler);
            }
        }
        waiterQueue.clear();
        waiter = None();
        initializer = None();
    }
}
function getAppEnvInitParams() {
    const el = getCurrentScript();
    if (!el) {
        return Err(new ConfigurationError({
            message: 'Отсутствует ссылка на текущий скрипт',
            domain: NAMESPACE + '.getAppEnvInitParams',
        }));
    }
    const src = el.getAttribute('src') || el.dataset['src'];
    const url = scriptSrcToUrl(src);
    const result = initByJSDump(el, url)
        .orElse(() => initByDataAttrs(el))
        .orElse(() => initByURLSearchParams(el, url));
    if (result.isNone()) {
        if (process.env.NODE_ENV === 'development') {
            return Err(new InitializationError({
                message: `Не удалось иницилизировать @cian/core/env/browser. Причины:\n` +
                    `- Вы обновили не все библиотеки SDK, а только часть из них. Попробуйте обновить через скрипт: https://ci-gitea.cian.tech/cianmedia/frontend-sdk/src/branch/master/shell\n` +
                    `- Ранее использовалась утилита setup() из @cian/config, вы ее удалили и теперь получили эту ошибку. ` +
                    `Скорее всего ассеты микросервиса генерируются или встраиваются в HTML каким-то нестандартным способом, без использования @cian/web AssetsProvider. ` +
                    `Например, через библиотеку @loadable. В этом случае вам нужно добавить к элементу ${src ? `<script src="${src}">` : 'бандла'} системные data-атрибуты. Их можно добавить через утилиту import { addSdkInitAttrs } from '@cian/web/node/assets';\n` +
                    `Если вам не помогла эта информация, обратитесь к Платформе.`,
                domain: NAMESPACE + '.getAppEnvInitParams',
            }));
        }
        return Err(new InitializationError({
            message: `Не удалось получить данные для инициализации AppEnv`,
            domain: NAMESPACE + '.getAppEnvInitParams',
        }));
    }
    const params = result.Some;
    params.extraDataParameters = new Map(intoIterable(el.dataset));
    if (url) {
        params.extraUrlParameters = new Map(url.searchParams);
        params.currentSrcUrl = url.toString();
    }
    return Ok(params);
}
function getCurrentScript() {
    if (document.currentScript) {
        return document.currentScript;
    }
    // oxlint-disable-next-line typescript/ban-ts-comment
    // @ts-ignore TS не нравится `import.meta`, однако он затем обрабатывается rollup
    return Array.from(document.scripts).find(s => s.src === import.meta.url) ?? null;
}
function initByJSDump(el, src) {
    if (!src) {
        return None();
    }
    const globalObjectKey = el.dataset[CLIENT_GLOBAL_OBJECT_KEY];
    const appEnvDumps = globalObjectKey
        ? window[globalObjectKey]
        : window[CLIENT_APP_ENV_DUMP_KEY];
    if (!appEnvDumps) {
        return None();
    }
    const appEnvInitParams = appEnvDumps[src.pathname];
    if (!appEnvInitParams || !appEnvInitParams.application) {
        return None();
    }
    return Some(Object.fromEntries(iteratorMap(intoIterable(appEnvInitParams), ([k, v]) => [
        k,
        decodeURIComponent(v),
    ])));
}
function initByDataAttrs(el) {
    let application = el.dataset[CORE_APP_ENV_KEYS_ATTR_MAP.application];
    if (!application) {
        application = el.dataset.projectname;
        if (!application) {
            return None();
        }
        if (process.env.NODE_ENV === 'development') {
            logger.warn(new ConfigurationError({
                message: 'Используется устаревший атрибут инициализации "data-projectname". ' +
                    'Скорее всего ассет был создан вручную, через утилиты @cian/web/node/assets. ' +
                    'Вам необходимо выполнить действия:\n' +
                    '- В случае микрофронтов рекомендуется использовать хелперы для создания API микрофронтов в @cian/web\n' +
                    '- В случае микросервиса рекомендуется работать с ассетами через AssetsProvider из @cian/web\n' +
                    '- Если предыдущие варианты трудно выполнимы, то воспользуйтесь утилитами инициализации из @cian/web/assets\n',
                domain: NAMESPACE,
            }));
        }
    }
    const version = el.dataset[CORE_APP_ENV_KEYS_ATTR_MAP.version];
    const mcsRunEnv = el.dataset[CORE_APP_ENV_KEYS_ATTR_MAP.mcsRunEnv];
    const versionCommit = el.dataset[CORE_APP_ENV_KEYS_ATTR_MAP.versionCommit];
    return Some({ application, version, mcsRunEnv, versionCommit });
}
function initByURLSearchParams(el, src) {
    if (!src) {
        return None();
    }
    let application = src.searchParams.get(CORE_APP_ENV_KEYS_QUERY_MAP.application);
    if (!application) {
        application = src.searchParams.get('projectName');
        if (!application) {
            return None();
        }
        if (process.env.NODE_ENV === 'development') {
            logger.warn(new ConfigurationError({
                message: 'Используется устаревший атрибут инициализации "projectName". ' +
                    'Скорее всего ассет был создан вручную, через утилиты @cian/web/node/assets. ' +
                    'Вам необходимо выполнить действия:\n' +
                    '- В случае микрофронтов рекомендуется использовать хелперы для создания API микрофронтов в @cian/web\n' +
                    '- В случае микросервиса рекомендуется работать с ассетами через AssetsProvider из @cian/web\n' +
                    '- Если предыдущие варианты трудно выполнимы, то воспользуйтесь утилитами инициализации из @cian/web/assets\n',
                domain: NAMESPACE,
            }));
        }
    }
    const version = src.searchParams.get(CORE_APP_ENV_KEYS_QUERY_MAP.version) || undefined;
    const mcsRunEnv = src.searchParams.get(CORE_APP_ENV_KEYS_QUERY_MAP.mcsRunEnv) || undefined;
    const versionCommit = src.searchParams.get(CORE_APP_ENV_KEYS_QUERY_MAP.versionCommit) || undefined;
    return Some({ application, version, mcsRunEnv, versionCommit });
}
function scriptSrcToUrl(src) {
    if (typeof src !== 'string') {
        return undefined;
    }
    if (src.startsWith('//')) {
        const protocol = window.location.protocol;
        return new URL(`${protocol}${src}`);
    }
    return new URL(src);
}

export { __cleanAppEnv, createAppEnv, getAppEnv, initAppEnv, waitForAppEnvInit };
//# sourceMappingURL=env.js.map
